import { useMemo, useState } from 'react'
import { ThemeProvider } from '@mui/material/styles'
import { Box, CssBaseline, Typography } from '@mui/material'
import { Prescription } from '../../../types/models/Presc'
import { colorTokens, latestTheme } from '../../../shared/styles/theme'
import { foldByOcrRequestEvent } from '../../medilab/latest/list/queue'
import { DetailPane } from './DetailPane'
import { Layout } from './Layout'
import { Sidebar } from './Sidebar'
import { AnnotatorPharmacy } from '../apis/annotatorView'
import {
  useAnnotatorPharmaciesQuery,
  useAnnotatorPrescriptionsQuery,
  useRequestIdLookup,
} from './useAnnotatorView'

/**
 * annotator の閲覧（latest UI）のエントリポイント。
 * apps/annotator/App.tsx から use_legacy_ui=false のときだけ分岐して入る。
 *
 * **顧客と同じ見え方で、書けるのは医療機関の様式（フォーマット設定）と旗（この紙で見つけた誤り）だけの面。**
 * できるのは薬局切替・リクエストID検索・様式の判断・旗の 4 つ。読取結果の修正は開けない。
 * 顧客側から借りるのは副作用を持たない部品だけ（ImagePane / list/queue / fields / FieldRow /
 * BrandMark / styles）で、データ源（usePrescriptionsQuery）も書き込み（useEditField / useReportField /
 * DetailFooter）も import しない。**顧客データへ書くコードはこの面のバンドルに入らない。**
 * 書き込みは apis/formatSetting.ts と apis/reports.ts の 2 本に隔離してある（後者は annotator 起源の
 * OCRError だけを触る）。
 *
 * 「いま開いている紙」は `openId`（ocr_request_event_id）の 1 状態。一覧の行を選んでも ID で引いても
 * ここに入り、右ペインの tablist 行に全 ID が出る（log/62 §2 C）。
 *
 * 面は「詳細」1 つで、一覧は Sidebar に置く（格納可能）。**TopBar は置かない**
 * （04-apps/mamoru.md 判断2）。「読み取り専用であること」と「どの店を見ているか」は
 * WorkspaceRow の段階ラベルと薬局セレクタが持つので、そのための帯を別に立てない。
 */
const MediLabAnnotatorLatestApp = () => {
  const [pharmacyKeyword, setPharmacyKeyword] = useState('')
  const [selectedPharmacy, setSelectedPharmacy] =
    useState<AnnotatorPharmacy | null>(null)
  const [openId, setOpenId] = useState<string | null>(null)

  const { data: pharmacyData } = useAnnotatorPharmaciesQuery(pharmacyKeyword)
  const { data: listData, isPending: isListPending } =
    useAnnotatorPrescriptionsQuery(selectedPharmacy?.id ?? null)
  const requestIdLookup = useRequestIdLookup()

  const rows = useMemo(
    () => foldByOcrRequestEvent(listData?.prescriptions ?? []),
    [listData]
  )

  // 一覧が全部でないなら黙って減らさず、そう言う。
  const listNotice = useMemo(() => {
    if (!listData) return null
    const parts: string[] = []
    if (listData.has_more) {
      parts.push(
        `最新 ${listData.prescriptions.length} 件のみ表示（期間内 ${listData.total_events} 件）`
      )
    }
    if (listData.skipped > 0) {
      parts.push(`${listData.skipped} 件は形が壊れていて表示できません`)
    }
    return parts.length ? parts.join(' ／ ') : null
  }, [listData])

  // 開いている紙は一覧に居ればその行（行も光る）、居なければ ID で引いたもの。
  const prescription: Prescription | undefined =
    rows.find((presc) => presc.ocr_request_event_id === openId) ??
    (requestIdLookup.found?.ocr_request_event_id === openId
      ? requestIdLookup.found
      : undefined)

  return (
    <ThemeProvider theme={latestTheme}>
      <CssBaseline />
      <Layout
        sidebar={({ onCollapse }) => (
          <Sidebar
            pharmacies={pharmacyData?.pharmacies ?? []}
            pharmaciesHasMore={pharmacyData?.has_more ?? false}
            listNotice={listNotice}
            pharmacyKeyword={pharmacyKeyword}
            onPharmacyKeywordChange={setPharmacyKeyword}
            selectedPharmacy={selectedPharmacy}
            onSelectPharmacy={(pharmacy) => {
              setSelectedPharmacy(pharmacy)
              setOpenId(null)
              requestIdLookup.clear()
            }}
            prescriptions={listData?.prescriptions ?? []}
            formatStatus={listData?.format_status ?? {}}
            isListPending={isListPending}
            selectedId={openId}
            onSelect={(ocrRequestEventId) => {
              // 一覧から別の紙を開いたら、前の ID 検索の失敗の一言は用済み
              requestIdLookup.clear()
              setOpenId(ocrRequestEventId)
            }}
            onLookupRequestId={async (requestId) => {
              const found = await requestIdLookup.lookup(requestId)
              if (found) setOpenId(found.ocr_request_event_id)
              return found !== null
            }}
            requestIdError={requestIdLookup.error}
            isLookingUp={requestIdLookup.isSearching}
            onCollapse={onCollapse}
          />
        )}
      >
        {prescription ? (
          <DetailPane prescription={prescription} />
        ) : (
          <Box
            sx={{
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            {/* empty-states.md 型1「まだ何も選ばれていない」= 1 文だけ置く */}
            <Typography sx={{ fontSize: 14, color: colorTokens.fg.faint }}>
              {selectedPharmacy
                ? '処方箋を選んでください'
                : '薬局を選んでください'}
            </Typography>
          </Box>
        )}
      </Layout>
    </ThemeProvider>
  )
}

export default MediLabAnnotatorLatestApp
